import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CancelledError,
  DisallowedContentError,
  NoTargetLanguageError,
  refusalMessage,
  useUploadMutation,
} from "./queries";
import { useLoggedIn } from "./firebase";
import { lstr } from "./localization";
import { ProgressOverlay } from "./ProgressOverlay";
import { EmailVerificationNotice } from "./EmailVerificationNotice";
import { PiClipboardText } from "react-icons/pi";

// MAX_CHARS mirrors upload.MaxInputChars on the Go side; the backend
// re-validates with utf8.RuneCountInString so any client-side bypass is
// caught.
const MAX_CHARS = 10_000;

export function UploadView({ l, r }: { l: string; r: string }) {
  const [text, setText] = useState<string>("");
  const navigate = useNavigate();
  const upload = useUploadMutation();
  const loggedIn = useLoggedIn();
  const abortRef = useRef<AbortController | null>(null);

  // UI chrome is rendered in the user's mother tongue (`l`), same convention
  // as the rest of the app. `r` is only used for the actual upload request.
  const strings = lstr(l);

  // Counted in code points like the backend's rune count. Over-long text is
  // kept and flagged rather than cut off (no maxLength), so a paste is never
  // silently truncated.
  const textLength = Array.from(text).length;
  const trimmedEmpty = text.trim() === "";
  const tooLong = textLength > MAX_CHARS;

  const handleSubmit = () => {
    if (!loggedIn) {
      navigate("/login");
      return;
    }
    // Allow submitting from the error state (retry); block only while a
    // request is in flight or when we're about to navigate after success.
    if (upload.isPending || upload.isSuccess || tooLong) return;
    const trimmed = text.trim();
    if (trimmed === "") return;
    abortRef.current = new AbortController();
    upload.mutate({ text: trimmed, r, signal: abortRef.current.signal });
  };

  const cancelUpload = () => {
    abortRef.current?.abort();
  };

  useEffect(() => {
    if (upload.isSuccess) {
      navigate(`/generated/${upload.data.id}`);
    }
  }, [upload.isSuccess, upload.data, navigate]);

  // Treat cancellation as a silent reset - same pattern as generate/scan -
  // so the textarea stays editable and the button label returns to "Upload"
  // instead of an error state.
  useEffect(() => {
    if (upload.isError && upload.error instanceof CancelledError) {
      upload.reset();
    }
  }, [upload.isError, upload.error, upload]);

  // Errors are shown in a red line above the submit button; the button
  // itself always keeps its action label so it stays tappable for a retry.
  // CancelledError is excluded: the reset effect above clears it, and this
  // guard prevents a one-frame flash of the generic message before that
  // effect runs.
  const errorMessage = (() => {
    if (!upload.isError || upload.error instanceof CancelledError) return null;
    const err = upload.error;
    if (err instanceof DisallowedContentError)
      return strings.upload_error_disallowed;
    if (err instanceof NoTargetLanguageError)
      return strings.upload_error_no_target_text;
    return refusalMessage(err, strings) ?? strings.upload_error_generic;
  })();

  const buttonLabel = !loggedIn
    ? strings.upload_login_prompt
    : upload.isPending
      ? strings.upload_in_progress
      : strings.upload_submit;

  const canSubmit =
    !loggedIn || (!upload.isPending && !trimmedEmpty && !tooLong);

  const counter = (
    <span
      className={
        tooLong
          ? "text-xs font-semibold text-error"
          : "text-xs tabular-nums text-secondary-text"
      }
    >
      {textLength.toLocaleString()} / {MAX_CHARS.toLocaleString()}
    </span>
  );

  return (
    <div className="flex flex-col gap-8">
      {upload.isPending && (
        <ProgressOverlay
          l={l}
          message={strings.upload_overlay_message}
          icon={<PiClipboardText />}
          onCancel={cancelUpload}
        />
      )}
      <header>
        <h1 className="font-literata text-[32px] font-bold leading-tight tracking-tight text-main-text md:text-4xl">
          {strings.upload_title_pre}{" "}
          <span className="text-primary">{strings.upload_title_post}</span>
        </h1>
        <p className="mt-2 max-w-lg text-secondary-text">
          {strings.home_create_upload_description}
        </p>
      </header>

      <EmailVerificationNotice l={l} />

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="eyebrow">{strings.upload_textarea_heading}</h2>
          {counter}
        </div>
        <textarea
          value={text}
          onChange={(e) => {
            if (upload.isError) upload.reset();
            setText(e.target.value);
          }}
          rows={14}
          placeholder={strings.upload_textarea_placeholder}
          disabled={upload.isPending}
          aria-invalid={tooLong}
          className="input resize-y p-4 font-literata text-[17px] leading-relaxed disabled:opacity-60"
        />
        {tooLong && (
          <div className="mt-2 text-sm font-semibold text-error">
            {strings.upload_too_long}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        {errorMessage && (
          <div
            role="alert"
            className="text-sm font-semibold text-error text-center"
          >
            {errorMessage}
          </div>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          className="btn-primary w-full py-4 text-lg"
          disabled={!canSubmit}
        >
          {buttonLabel}
        </button>
      </section>
    </div>
  );
}
